import React, { useState, useEffect, ReactElement } from 'react';
import CareProSearchDropdown from 'Components/CareProSearchDropdown';
import { _attr } from "Util/Utilities";
import { CareProType } from 'Components/CareProSearchDropdown/types';
import { DropdownDataType } from 'Screens/CareOwner/SubComponents/CareProPreferences/PreferredCareProSection/types';
import { searchCareProsList } from 'Screens/CareOwner/SubComponents/CareProPreferences/PreferredCareProSection/searchCareProsList';
import './style.css'

export type PreferredCPSubViewDropDownType = {
  home_personal_care?: Array<CareProType>;
  home_medical?: Array<CareProType>;
  home_therapy?: Array<CareProType>;
}

export type PreferredCareProsSubViewPropsType = {
  isReadOnlyMode: boolean | undefined;
  isCurrentViewTypePreferredCarePros: boolean;
  thereAreHomePersonalCareServicesSelected: boolean;
  thereAreHomeTherapyServicesSelected: boolean;
  thereAreHomeMedicalServicesSelected: boolean;
  apiDropdownData: PreferredCPSubViewDropDownType;
  previousVisitCarePros?: PreferredCPSubViewDropDownType;
  currentVisitCarePros: PreferredCPSubViewDropDownType;
  onChange: (data: any) => void;
}

const organizeDropdownDataIntoHasVisitedAndNoVisits = (dropdownData: Array<CareProType>) => {
  const hasNotVisited: Array<CareProType> = [];
  const hasVisited: Array<CareProType> = [];

  const dataClone: Array<CareProType> = structuredClone(dropdownData);

  dataClone.forEach((carepro) => {
    if (carepro.has_visited_cr_before) {
      hasVisited.push(carepro);
    } else {
      hasNotVisited.push(carepro);
    }
  });

  return { preferred_cps_list_has_not_visited: hasNotVisited, preferred_cps_list_has_visited: hasVisited };
}

/*
  NOTE: This process is opt-out. That means that all dropdown data initially is considered as selected Care Pros.
        Valid as of October 2022.
*/

const PreferredCareProsSubView = (props: PreferredCareProsSubViewPropsType): ReactElement | null => {
  const {
    isReadOnlyMode,
    isCurrentViewTypePreferredCarePros,
    thereAreHomePersonalCareServicesSelected,
    thereAreHomeTherapyServicesSelected,
    thereAreHomeMedicalServicesSelected,
    apiDropdownData,
    previousVisitCarePros,
    currentVisitCarePros,
    onChange
  } = props;

  const shouldDisplayThisComponent: boolean = isCurrentViewTypePreferredCarePros && !isReadOnlyMode;

  const defaultEmptyDropdown = { preferred_cps_list_has_not_visited: [], preferred_cps_list_has_visited: [] };
  // Dropdown list that has been truncated to match search query
  const [searchedDropdownList, setSearchedDropdownList] = useState<DropdownDataType>(defaultEmptyDropdown);
  // The dropdown values (will always contain the full list from API)
  const [personalDropdownList, setPersonalDropdownList] = useState<DropdownDataType>(defaultEmptyDropdown);
  const [therapyDropdownList, setTherapyDropdownList] = useState<DropdownDataType>(defaultEmptyDropdown);
  const [medicalDropdownList, setMedicalDropdownList] = useState<DropdownDataType>(defaultEmptyDropdown);
  // The Care Pros the HO/LO has selected from the dropdown
  const [homePersonalCarePros, setHomePersonalCarePros] = useState<Array<CareProType>>([]);
  const [homeTherapyCarePros, setHomeTherapyCarePros] = useState<Array<CareProType>>([]);
  const [homeMedicalCarePros, setHomeMedicalCarePros] = useState<Array<CareProType>>([]);

  // There can only be one service selected at a time
  const isPersonalFieldDisabled = thereAreHomeTherapyServicesSelected || thereAreHomeMedicalServicesSelected;
  const isTherapyFieldDisabled = thereAreHomePersonalCareServicesSelected || thereAreHomeMedicalServicesSelected;
  const isMedicalFieldDisabled = thereAreHomePersonalCareServicesSelected || thereAreHomeTherapyServicesSelected;

  const _initialSetupOfDropdownDataForDisplay = () => {
    let data = null;
    if (apiDropdownData?.home_personal_care && !isPersonalFieldDisabled) {
      data = organizeDropdownDataIntoHasVisitedAndNoVisits(apiDropdownData.home_personal_care);
      setPersonalDropdownList(data);
    };

    if (apiDropdownData?.home_therapy && !isTherapyFieldDisabled) {
      data = organizeDropdownDataIntoHasVisitedAndNoVisits(apiDropdownData.home_therapy);
      setTherapyDropdownList(data);
    };

    if (apiDropdownData?.home_medical && !isMedicalFieldDisabled) {
      data = organizeDropdownDataIntoHasVisitedAndNoVisits(apiDropdownData.home_medical);
      setMedicalDropdownList(data);
    };

    if (data === null) {
      setSearchedDropdownList(defaultEmptyDropdown);
      return;
    }

    setSearchedDropdownList(data);
  }

  useEffect(() => {
    _initialSetupOfDropdownDataForDisplay();
  }, [apiDropdownData, isPersonalFieldDisabled, isTherapyFieldDisabled, isMedicalFieldDisabled]);

  const _setInitialPersonalCarePros = (careProList?: Array<CareProType>) => {
    const doesListExist = careProList && careProList.length > 0;
    if (!doesListExist) {
      return;
    }

    setHomePersonalCarePros(careProList);
  };

  const _setInitialHomeTherapyCarePros = (careProList?: Array<CareProType>) => {
    const doesListExist = careProList && careProList.length > 0;
    if (!doesListExist) {
      return;
    }

    setHomeTherapyCarePros(careProList);
  };

  const _setInitialHomeMedicalCarePros = (careProList?: Array<CareProType>) => {
    const doesListExist = careProList && careProList.length > 0;
    if (!doesListExist) {
      return;
    }

    setHomeMedicalCarePros(careProList);
  };

  const _setInitialSelectedCarePros = () => {

    if (currentVisitCarePros && Object.keys(currentVisitCarePros).length > 0) {
      _setInitialPersonalCarePros(currentVisitCarePros.home_personal_care);
      _setInitialHomeTherapyCarePros(currentVisitCarePros.home_therapy);
      _setInitialHomeMedicalCarePros(currentVisitCarePros.home_medical);
      return;
    }

    if (previousVisitCarePros) {
      _setInitialPersonalCarePros(previousVisitCarePros.home_personal_care);
      _setInitialHomeTherapyCarePros(previousVisitCarePros.home_therapy);
      _setInitialHomeMedicalCarePros(previousVisitCarePros.home_medical);
      return;
    }

    if (apiDropdownData) {
      _setInitialPersonalCarePros(apiDropdownData.home_personal_care);
      _setInitialHomeTherapyCarePros(apiDropdownData.home_therapy);
      _setInitialHomeMedicalCarePros(apiDropdownData.home_medical);
    }
  }

  useEffect(() => {
    _setInitialSelectedCarePros();
  }, [currentVisitCarePros, previousVisitCarePros, apiDropdownData]);

  const _updateMainFormDataWithSelectedCareProIDs = () => {

    // We're using destructuring here to only include the service that is currently selected and ignore the rest
    const payload = {
      ...thereAreHomePersonalCareServicesSelected && { home_personal_care: homePersonalCarePros },
      ...thereAreHomeTherapyServicesSelected && { home_therapy: homeTherapyCarePros },
      ...thereAreHomeMedicalServicesSelected && { home_medical: homeMedicalCarePros },
    };

    onChange(payload);
  }

  useEffect(() => {
    _updateMainFormDataWithSelectedCareProIDs();
  }, [homePersonalCarePros, homeTherapyCarePros, homeMedicalCarePros,
    thereAreHomePersonalCareServicesSelected, thereAreHomeTherapyServicesSelected, thereAreHomeMedicalServicesSelected]);

  const _careProIsAlreadyInTheList = (carepro_id: string, list: Array<CareProType>): boolean => {
    const matchingCarePro = list.find((carepro) => carepro.id === carepro_id);
    if (matchingCarePro) {
      return true;
    }
    return false;
  }


  const _addCareProToPersonalList = (carepro: CareProType) => {
    if (_careProIsAlreadyInTheList(carepro.id, homePersonalCarePros)) {
      return;
    }
    setHomePersonalCarePros([...homePersonalCarePros, carepro]);
  }


  const _addCareProToTherapyList = (carepro: CareProType) => {
    if (_careProIsAlreadyInTheList(carepro.id, homeTherapyCarePros)) {
      return;
    }
    setHomeTherapyCarePros([...homeTherapyCarePros, carepro]);
  }


  const _addCareProToMedicalList = (carepro: CareProType) => {
    if (_careProIsAlreadyInTheList(carepro.id, homeMedicalCarePros)) {
      return;
    }
    setHomeMedicalCarePros([...homeMedicalCarePros, carepro]);
  }

  const _removeCareProFromPersonalList = (carepro: CareProType) => {
    let newHomePersonalCareProsList: Array<CareProType> = [];

    newHomePersonalCareProsList = homePersonalCarePros.filter(
      (personalCarePro: CareProType) => carepro.id !== personalCarePro.id)
    setHomePersonalCarePros(newHomePersonalCareProsList);
  }

  const _removeCareProFromTherapyList = (carepro: CareProType) => {
    let newHomeTherapyCareProsList: Array<CareProType> = [];

    newHomeTherapyCareProsList = homeTherapyCarePros.filter(
      (personalCarePro: CareProType) => carepro.id !== personalCarePro.id)
    setHomeTherapyCarePros(newHomeTherapyCareProsList);
  }

  const _removeCareProFromMedicalList = (carepro: CareProType) => {
    let newHomeMedicalCareProsList: Array<CareProType> = [];

    newHomeMedicalCareProsList = homeMedicalCarePros.filter(
      (personalCarePro: CareProType) => carepro.id !== personalCarePro.id)
    setHomeMedicalCarePros(newHomeMedicalCareProsList);
  }

  const _searchForCareProPersonal = (careProName: string) => {
    const { preferred_cps_list_has_not_visited, preferred_cps_list_has_visited } = personalDropdownList;
    const listThatMatchesSearch = searchCareProsList(careProName, preferred_cps_list_has_not_visited, preferred_cps_list_has_visited);

    setSearchedDropdownList(listThatMatchesSearch);
  }

  const _searchForCareProTherapy = (careProName: string) => {
    const { preferred_cps_list_has_not_visited, preferred_cps_list_has_visited } = therapyDropdownList;
    const listThatMatchesSearch = searchCareProsList(careProName, preferred_cps_list_has_not_visited, preferred_cps_list_has_visited);

    setSearchedDropdownList(listThatMatchesSearch);
  }

  const _searchForCareProMedical = (careProName: string) => {
    const { preferred_cps_list_has_not_visited, preferred_cps_list_has_visited } = medicalDropdownList;
    const listThatMatchesSearch = searchCareProsList(careProName, preferred_cps_list_has_not_visited, preferred_cps_list_has_visited);

    setSearchedDropdownList(listThatMatchesSearch);
  }

  const _renderTitle = (title: string): ReactElement => {
    return (<div className='preferred-cp-subview-title'>
      <span className='preferred-cp-subview-title-text'>{title}</span>
    </div>)
  }

  const _renderHomePersonalCareAndNursingSection = (): ReactElement => {
    return <>
      {_renderTitle('Home Personal Care & Nursing')}
      <div className='preferred-cp-subview-dropdown-container'>
        <CareProSearchDropdown
          dropdownDataPrimary={searchedDropdownList.preferred_cps_list_has_visited}
          dropdownDataSecondary={searchedDropdownList.preferred_cps_list_has_not_visited}
          placeholder={"Enter Care Pro name"}
          error={false}
          value={homePersonalCarePros}
          onChange={(carepro) => _addCareProToPersonalList(carepro)}
          onChangeText={(searchQuery) => _searchForCareProPersonal(searchQuery)}
          onChangeChip={(carepro) => _removeCareProFromPersonalList(carepro)}
          disabled={isPersonalFieldDisabled}
          required={false}
          onBlur={() => { }}
          popupWidth={380}
        /></div>
    </>
  }

  const _renderHomeTherapySection = (): ReactElement => {
    return <>
      {_renderTitle('Home Therapy')}
      <div className='preferred-cp-subview-dropdown-container'>
        <CareProSearchDropdown
          dropdownDataPrimary={searchedDropdownList.preferred_cps_list_has_visited}
          dropdownDataSecondary={searchedDropdownList.preferred_cps_list_has_not_visited}
          placeholder={"Enter Care Pro name"}
          error={false}
          value={homeTherapyCarePros}
          onChange={(carepro) => _addCareProToTherapyList(carepro)}
          onChangeText={(searchQuery) => _searchForCareProTherapy(searchQuery)}
          onChangeChip={(carepro) => _removeCareProFromTherapyList(carepro)}
          disabled={isTherapyFieldDisabled}
          required={false}
          onBlur={() => { }}
          popupWidth={380}
        /></div>
    </>
  }

  const _renderHomeMedicalSection = (): ReactElement => {
    return <>
      {_renderTitle('Home Medical')}
      <div className='preferred-cp-subview-dropdown-container'>
        <CareProSearchDropdown
          dropdownDataPrimary={searchedDropdownList.preferred_cps_list_has_visited}
          dropdownDataSecondary={searchedDropdownList.preferred_cps_list_has_not_visited}
          placeholder={"Enter Care Pro name"}
          error={false}
          value={homeMedicalCarePros}
          onChange={(carepro) => _addCareProToMedicalList(carepro)}
          onChangeText={(searchQuery) => _searchForCareProMedical(searchQuery)}
          onChangeChip={(carepro) => _removeCareProFromMedicalList(carepro)}
          disabled={isMedicalFieldDisabled}
          required={false}
          onBlur={() => { }}
          popupWidth={380}
        /></div>
    </>
  }

  if (shouldDisplayThisComponent) {
    return (
      <div data-visible={_attr(isCurrentViewTypePreferredCarePros)} className='preferred-cp-subview'>
        {_renderHomePersonalCareAndNursingSection()}
        {_renderHomeTherapySection()}
        {_renderHomeMedicalSection()}
      </div>
    )
  }

  return null;
}

export default PreferredCareProsSubView;